import React, { forwardRef, useEffect, useState } from 'react';
import { cn } from '../../utils/cn';

export interface ProgressIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  progress: number;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'success' | 'warning' | 'error';
  variant?: 'bar' | 'circular' | 'dots';
  animate?: boolean;
  showPercentage?: boolean;
  label?: string;
}

const ProgressIndicator = forwardRef<HTMLDivElement, ProgressIndicatorProps>(
  (
    {
      progress,
      size = 'md',
      color = 'primary',
      variant = 'bar',
      animate = true,
      showPercentage = false,
      label,
      className,
      ...props
    },
    ref,
  ) => {
    const [displayProgress, setDisplayProgress] = useState(0);

    useEffect(() => {
      if (animate) {
        const timer = setTimeout(() => {
          setDisplayProgress(progress);
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setDisplayProgress(progress);
      }
    }, [progress, animate]);

    const sizeClasses = {
      sm: variant === 'bar' ? 'h-1' : variant === 'circular' ? 'w-8 h-8' : 'w-1 h-1',
      md: variant === 'bar' ? 'h-2' : variant === 'circular' ? 'w-12 h-12' : 'w-2 h-2',
      lg: variant === 'bar' ? 'h-3' : variant === 'circular' ? 'w-16 h-16' : 'w-3 h-3',
    };

    const colorClasses = {
      primary: 'text-[color:var(--color-primary-600)]',
      success: 'text-[color:var(--color-success-600)]',
      warning: 'text-[color:var(--color-warning-600)]',
      error: 'text-[color:var(--color-error-600)]',
    };

    const progressColorClasses = {
      primary: 'bg-[color:var(--color-primary-500)]',
      success: 'bg-[color:var(--color-success-500)]',
      warning: 'bg-[color:var(--color-warning-500)]',
      error: 'bg-[color:var(--color-error-500)]',
    };

    if (variant === 'circular') {
      const radius = 20;
      const circumference = 2 * Math.PI * radius;
      const strokeDashoffset = circumference - (displayProgress / 100) * circumference;

      return (
        <div className={cn('relative', className)} ref={ref} {...props}>
          {label && (
            <div className="text-sm font-medium text-neutral-700 mb-2 text-center">{label}</div>
          )}
          <div className="relative inline-flex items-center justify-center">
            <svg className={cn('transform -rotate-90', sizeClasses[size])} viewBox="0 0 44 44">
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-neutral-200"
              />
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className={cn('transition-all duration-500 ease-out', colorClasses[color])}
                style={{
                  strokeLinecap: 'round',
                }}
              />
            </svg>
            {showPercentage && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={cn('text-xs font-semibold', colorClasses[color])}>
                  {Math.round(displayProgress)}%
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (variant === 'dots') {
      const dots = Array.from({ length: 5 }, (_, i) => {
        const isActive = (i + 1) * 20 <= displayProgress;
        return (
          <div
            key={i}
            className={cn(
              'rounded-full transition-all duration-300 ease-out',
              sizeClasses[size],
              isActive ? progressColorClasses[color] : 'bg-neutral-200',
              animate && isActive && 'animate-pulse',
            )}
          />
        );
      });

      return (
        <div className={cn('flex items-center space-x-1', className)} ref={ref} {...props}>
          {label && <span className="text-sm font-medium text-neutral-700 mr-2">{label}</span>}
          {dots}
          {showPercentage && (
            <span className={cn('text-xs font-semibold ml-2', colorClasses[color])}>
              {Math.round(displayProgress)}%
            </span>
          )}
        </div>
      );
    }

    // Bar variant
    return (
      <div className={cn('w-full', className)} ref={ref} {...props}>
        {label && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-neutral-700">{label}</span>
            {showPercentage && (
              <span className={cn('text-xs font-semibold', colorClasses[color])}>
                {Math.round(displayProgress)}%
              </span>
            )}
          </div>
        )}
        <div
          className={cn('w-full bg-neutral-200 rounded-full overflow-hidden', sizeClasses[size])}
        >
          <div
            className={cn(
              'h-full transition-all duration-500 ease-out rounded-full',
              progressColorClasses[color],
              animate && 'animate-shimmer',
            )}
            style={{ width: `${Math.min(100, Math.max(0, displayProgress))}%` }}
          />
        </div>
      </div>
    );
  },
);

ProgressIndicator.displayName = 'ProgressIndicator';

export { ProgressIndicator };
